import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import { supportTicketFilterOptions as defaultFilterOptions } from "../support-ticket-constants";
import { Formik, Form } from "formik";
import dayjs from "dayjs";
import { SupportTicketFilterOptionsProps } from "../types";
import DateRaisedFilter from "./date-raised-filter";
import PartnerFilter from "./partner-filter";
import "./support-ticket-filter-options.scss";
import SubscriptionPlanFilter from "../../tenant/tenant-list/tenant-filter-options/subscription-plan";
import "../../tenant/tenant-list/tenant-filter-options/filter-options-tenant.scss";
import SingleSelectAutocomplete from "../../leads/lead-assignment/assignement-rules/single-option-select";

const SupportTicketFilterOptions: React.FC<SupportTicketFilterOptionsProps> = ({
  open,
  onClose,
  onApplyFilters,
  filters,
  companyNames = [],
  senders = [],
}) => {
  const sendersList = senders?.map((sender) => ({
    label: sender,
    value: sender,
  }));
  const [filterOptions, setFilterOptions] = useState(() =>
    defaultFilterOptions.map((opt) => ({ ...opt }))
  );

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Date Raised") {
            setFieldValue("dateRaisedStart", null);
            setFieldValue("dateRaisedEnd", null);
          } else if (label === "Date Closed") {
            setFieldValue("dateClosedStart", null);
            setFieldValue("dateClosedEnd", null);
          } else {
            const key = toCamelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);

  const toCamelCase = (label: string) =>
    label
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, "");

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Date Raised") {
        values["dateRaisedStart"] = null;
        values["dateRaisedEnd"] = null;
      } else if (option.label === "Date Closed") {
        values["dateClosedStart"] = null;
        values["dateClosedEnd"] = null;
      } else {
        const key = toCamelCase(option.label);
        if (option.label === "Status") {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Date Raised") {
            acc["dateRaisedStart"] = values["dateRaisedStart"];
            acc["dateRaisedEnd"] = values["dateRaisedEnd"];
          } else if (curr.label === "Date Closed") {
            acc["dateClosedStart"] = values["dateClosedStart"];
            acc["dateClosedEnd"] = values["dateClosedEnd"];
          } else {
            const key = toCamelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");
    const today = dayjs().endOf("day");

    if (!Array.isArray(filterOptions)) return errors;

    filterOptions.forEach((option) => {
      if (!option?.value) return;

      const key = toCamelCase(option.label);

      const handleDateValidation = (startKey: string, endKey: string) => {
        const startRaw = values[startKey];
        const endRaw = values[endKey];
        const startDate = startRaw ? dayjs(startRaw) : null;

        const endDate = endRaw ? dayjs(endRaw) : null;

        // --- Start Date Validation ---
        if (startDate && !startDate?.isValid()) {
          errors[startKey] = "Invalid start date";
        } else if (!startDate) {
          errors[startKey] = "Start date is required";
        } else if (startDate.isBefore(minDate)) {
          errors[startKey] = `Invalid start date`;
        } else if (startDate.isAfter(today)) {
          errors[startKey] = "Start date cannot be in the future";
        } else if (endDate && startDate.isAfter(endDate)) {
          errors[startKey] = "Start date cannot be after end date";
        } else {
          delete errors?.[startKey];
        }

        // --- End Date Validation ---
        if (endDate && !endDate?.isValid()) {
          errors[endKey] = "Invalid end date";
        } else if (!endDate) {
          errors[endKey] = "End date is required";
        } else if (endDate.isBefore(minDate)) {
          errors[endKey] = `Invalid end date`;
        } else if (endDate.isAfter(today)) {
          errors[endKey] = "End date cannot be in the future";
        } else if (startDate && endDate.isBefore(startDate)) {
          errors[endKey] = "End date cannot be before start date";
        }
      };

      if (option.label === "Date Raised") {
        handleDateValidation("dateRaisedStart", "dateRaisedEnd");
      } else if (option.label === "Date Closed") {
        handleDateValidation("dateClosedStart", "dateClosedEnd");
      } else {
        const fieldValue = values[key];
        if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          errors[key] = `${option.label} cannot be empty`;
        }
      }
    });
    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void,
    setValues?: (vals: Record<string, any>) => void
  ) => {
    switch (label) {
      case "Partner":
        return (
          <PartnerFilter
            isCompanyNameEnabled={true}
            companyNames={companyNames || []}
            value={values["partner"] || []}
            error={errors?.partner}
            onChange={(selectedValues) =>
              setFieldValue?.("partner", selectedValues)
            }
          />
        );
      case "Status":
        return (
          <SubscriptionPlanFilter
            value={
              Array.isArray(values["status"])
                ? values["status"]?.map((status: string) => {
                    if (status === "ReOpen") return "Reopen";
                    if (status === "InProgress") return "In Progress";
                    return status;
                  })
                : []
            }
            plans={["Open", "In Progress", "Closed", "Reopen"]}
            error={errors?.status}
            onChange={(val: string[]) => {
              const mappedValues = val.map((status) => {
                if (status === "Reopen") return "ReOpen";
                if (status === "In Progress") return "InProgress";
                return status;
              });
              setFieldValue?.("status", mappedValues);
            }}
            isMultiple={true}
            label="Select Status"
          />
        );
      case "Date Raised":
        return (
          <DateRaisedFilter
            startDate={values.dateRaisedStart}
            endDate={values.dateRaisedEnd}
            onDateRangeChange={(start, end) => {
              setValues?.((prev: any) => ({
                ...prev,
                dateRaisedStart: start,
                dateRaisedEnd: end,
              }));
            }}
            startDateError={
              touched?.dateRaisedStart && errors?.dateRaisedStart
                ? String(errors.dateRaisedStart)
                : undefined
            }
            endDateError={
              touched?.dateRaisedEnd && errors?.dateRaisedEnd
                ? String(errors.dateRaisedEnd)
                : undefined
            }
            startDateFieldName="dateRaisedStart"
            endDateFieldName="dateRaisedEnd"
          />
        );
      case "Date Closed":
        return (
          <DateRaisedFilter
            startDate={values.dateClosedStart}
            endDate={values.dateClosedEnd}
            onDateRangeChange={(start, end) => {
              setValues?.((prev: any) => ({
                ...prev,
                dateClosedStart: start,
                dateClosedEnd: end,
              }));
            }}
            startDateError={
              touched?.dateClosedStart && errors?.dateClosedStart
                ? String(errors.dateClosedStart)
                : undefined
            }
            endDateError={
              touched?.dateClosedEnd && errors?.dateClosedEnd
                ? String(errors.dateClosedEnd)
                : undefined
            }
            startDateFieldName="dateClosedStart"
            endDateFieldName="dateClosedEnd"
          />
        );
      case "Sender":
        return (
          <div className="sender-filter-wrapper">
            <SingleSelectAutocomplete
              options={sendersList}
              selectedValue={
                sendersList.find((opt) => opt.value === values.sender) || null
              }
              placeholder="Select Sender"
              onChange={(val: any) => {
                setFieldValue?.("sender", val?.value || "");
              }}
              noOptionsFoundText={"No matching senders found"}
              fieldName="afp-support-tickets-sender-filter"
            />
            {errors?.sender && <div className="error-message">{errors?.sender}</div>}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = defaultFilterOptions.map((opt) => {
      let key =
        opt.label === "Date Raised"
          ? "dateRaisedStart" in filters || "dateRaisedEnd" in filters
          : toCamelCase(opt.label) in filters;
      if (opt.label === "Date Closed") {
        key = "dateClosedStart" in filters || "dateClosedEnd" in filters;
      }
      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div
      className={`filter-drawer ${open ? "open" : ""} support-ticket-filter-options-wrapper`}
    >
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>

      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
          setValues,
        }) => {
          return (
            <Form className="drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox checked={option.value} />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      setValues
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        defaultFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({});
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default SupportTicketFilterOptions;
